import { z } from "zod";

export const ADS_EXPRESS_PORTRAIT_REFRAME_FLAG = "ads_express_916_via_45_reframe";
// Immutable payload identity, not a live switch. Absence means historical parity.
export const AUTOMATIC_PORTRAIT_BAND_PROMPT_VARIANT = "background_only_first_pass_v1" as const;
export type PortraitBandPromptVariant = typeof AUTOMATIC_PORTRAIT_BAND_PROMPT_VARIANT;
export const FOUR_FIVE_MARGIN_CONSTRAINT = "FINAL COMPOSITION CONSTRAINT: Produce a full-bleed 4:5 image. Every required text line, the complete CTA button including its whole shape, the offer and primary product detail must fit entirely within x=8%..92% and y=4%..90% of the canvas. Keep the CTA button's bottom edge no lower than 88% of the canvas height. Background photography and nonessential parts of a subject may extend to the edges. If the reference puts essential content outside these bounds, reposition individual elements. Do not copy the unsafe positions.";
export const manualReframeSettingsSchema = z.object({
  safeZoneEnabled: z.boolean(), enhancedRender: z.boolean(), seamBlend: z.boolean(),
});
export type ManualReframeSettings = z.infer<typeof manualReframeSettingsSchema>;

export const portraitReframeStateSchema = z.object({
  effectiveFlag: z.literal(true),
  // Absent on the exact manual recipe. Retained for already-admitted work.
  operationRecipe: z.literal("manual_45_then_reframe").optional(),
  reframeSettings: manualReframeSettingsSchema.optional(),
  // New automatic admissions reject flat bands; absent preserves saved recipes.
  requireOutpainted: z.literal(true).optional(),
  bandPromptVariant: z.literal(AUTOMATIC_PORTRAIT_BAND_PROMPT_VARIANT).optional(),
  extensionResult: z.record(z.unknown()).optional(),
  sourceIs45: z.boolean().optional(),
  constraintSnapshot: z.string().min(1).optional(),
  finalDimensions: z.object({ width: z.number().positive(), height: z.number().positive() }).optional(),
  stage: z.enum(["base", "reframe", "fallback", "complete"]),
  intermediateUrl: z.string().optional(),
  intermediateObjectKey: z.string().optional(),
  baseResult: z.record(z.unknown()).optional(),
  finalResult: z.record(z.unknown()).optional(),
  providerCosts: z.array(z.object({
    stage: z.string(), requestId: z.string(), endpoint: z.string(),
    model: z.string(), tier: z.string(),
    supplierCostUsd: z.null(),
    catalogEstimateUsd: z.number().nonnegative().nullable(),
  })).optional(),
  passOneStartedAt: z.number().optional(),
  passOneMs: z.number().optional(),
  reframeStartedAt: z.number().optional(),
  reframeMs: z.number().optional(),
  attempts: z.array(z.object({
    endpoint: z.string(),
    requestId: z.string().optional(),
    state: z.enum(["prepared", "submitting", "accepted", "failed", "complete"]),
    startedAt: z.number(),
    durationMs: z.number().optional(),
    failure: z.string().optional(),
    retryId: z.string().optional(),
    nanoResultUrl: z.string().optional(),
    providerStages: z.record(z.object({
      intentAt: z.number(), requestId: z.string().optional(),
    })).optional(),
  })),
  fallbackUsed: z.boolean(),
  fallbackReason: z.string().optional(),
});
export type PortraitReframeState = z.infer<typeof portraitReframeStateSchema>;

export function portraitReframeAdmission(enabled: boolean, reframeSettings?: ManualReframeSettings) {
  return enabled ? {
    renderMode: "via_45_reframe" as const,
    portraitReframe: {
      effectiveFlag: true as const,
      operationRecipe: "manual_45_then_reframe" as const,
      ...(reframeSettings ? { reframeSettings } : {}),
      ...(reframeSettings?.safeZoneEnabled ? { requireOutpainted: true as const } : {}),
      ...(reframeSettings?.safeZoneEnabled && reframeSettings.enhancedRender
        ? { bandPromptVariant: AUTOMATIC_PORTRAIT_BAND_PROMPT_VARIANT } : {}),
      stage: "base" as const,
      attempts: [],
      fallbackUsed: false,
    },
  } : {};
}

export function isManualPortraitRecipe(plan: {
  portraitReframe?: Pick<PortraitReframeState, "operationRecipe">;
} | null | undefined) {
  return plan?.portraitReframe?.operationRecipe === "manual_45_then_reframe";
}

export function isPortraitBase(plan: {
  renderMode?: string;
  portraitReframe?: PortraitReframeState;
} | null | undefined) {
  return plan?.renderMode === "via_45_reframe" && plan.portraitReframe?.stage === "base";
}

export function stripSavedPortraitGeometry(prompt: string | null | undefined, plan: {
  nativePromptSnapshot?: { instructions: string };
  portraitReframe?: { constraintSnapshot?: string };
} | null | undefined) {
  if (!prompt) return prompt;
  let result = prompt;
  for (const suffix of [plan?.portraitReframe?.constraintSnapshot, plan?.nativePromptSnapshot?.instructions]) {
    if (suffix && result.endsWith(`\n\n${suffix}`)) result = result.slice(0, -suffix.length - 2);
  }
  return result;
}
